import { jsx, Fragment } from "react/jsx-runtime";
import { memo, useMemo, createElement } from "react";
import { defaultDeserialize } from "./defaultDeserialize.js";
import { renderChildren } from "./renderChildren.js";
import { RichTextRendererError } from "./RichTextRendererError.js";
import { RichTextRenderMetadataContext } from "./RichTextRenderMetadataContext.js";
const RichTextRenderer = memo(function RichTextRenderer2({
  deserialize = defaultDeserialize,
  renderElement,
  renderLeaf,
  renderBlock = ({ children }) => /* @__PURE__ */ jsx(Fragment, { children }),
  attributeNamePrefix,
  ...sourceProps
}) {
  const normalizedSource = useMemo(() => {
    if ("source" in sourceProps && !("blocks" in sourceProps)) {
      return [
        {
          content: sourceProps.source ? deserialize(sourceProps.source) : {
            formatVersion: 0,
            children: []
          },
          id: void 0,
          referencesField: void 0,
          referenceDiscriminationField: void 0,
          referenceRenderers: {},
          references: void 0
        }
      ];
    }
    if (!("source" in sourceProps)) {
      const sourceField = sourceProps.sourceField ?? "source";
      const referencesField = sourceProps.referencesField ?? "references";
      const referenceDiscriminationField = sourceProps.referenceDiscriminationField ?? "type";
      return sourceProps.blocks.map((block) => {
        if (!(sourceField in block)) {
          throw new RichTextRendererError(
            `Found a block without a '${sourceField}' field. ` + (sourceProps.sourceField === void 0 ? `The 'sourceField' prop has not been supplied, and so '${sourceField}' was used as a default.` : `That is what the 'sourceField' prop has been set to, and so either this is a typo, or the data supplied is invalid.`)
          );
        }
        const source = block[sourceField];
        if (typeof source !== "string") {
          throw new RichTextRendererError(`Found a block whose source is not a string.`);
        }
        if (sourceProps.referencesField !== void 0 && !(referencesField in block)) {
          throw new RichTextRendererError(
            `The 'referencesField' prop is set to '${referencesField}' but a block without such field has been encountered. Unless this is just a typo, if you do not wish to use references, avoid supplying the 'referencesField' prop.`
          );
        }
        const references = block[referencesField];
        let normalizedReferences = void 0;
        if (references) {
          if (typeof Object(references)[Symbol.iterator] !== "function") {
            throw new RichTextRendererError(`The set of references must be an iterable!`);
          }
          normalizedReferences = /* @__PURE__ */ new Map();
          for (const reference of references) {
            if (!("id" in reference)) {
              throw new RichTextRendererError(`Found a reference without an id field.`);
            }
            if (!(referenceDiscriminationField in reference)) {
              throw new RichTextRendererError(
                `Found a reference without a '${referenceDiscriminationField}' field. ` + (sourceProps.referenceDiscriminationField === void 0 ? `The 'referenceDiscriminationField' prop has not been supplied, and so '${referenceDiscriminationField}' was used as a default.` : `That is what the 'referenceDiscriminationField' prop has been set to, and so either this is a typo, or the data supplied is invalid.`)
              );
            }
            normalizedReferences.set(reference.id, reference);
          }
        }
        return {
          id: typeof block.id === "string" ? block.id : void 0,
          content: deserialize(source),
          referenceDiscriminationField,
          referenceRenderers: sourceProps.referenceRenderers ?? {},
          referencesField,
          references: normalizedReferences
        };
      });
    }
    throw new RichTextRendererError(`RichTextRenderer: invalid data supplied.`);
  }, [deserialize, sourceProps]);
  return /* @__PURE__ */ jsx(Fragment, { children: normalizedSource.map((block, i) => /* @__PURE__ */ jsx(
    RichTextRenderMetadataContext.Provider,
    {
      value: {
        formatVersion: block.content.formatVersion,
        referenceRenderers: block.referenceRenderers,
        referencesField: block.referencesField,
        referenceDiscriminationField: block.referenceDiscriminationField,
        references: block.references
      },
      children: createElement(
        renderBlock,
        {
          block
        },
        renderChildren(block.content.children, {
          renderLeaf,
          renderElement,
          attributeNamePrefix
        })
      )
    },
    block.id ?? i
  )) });
});
export {
  RichTextRenderer
};
//# sourceMappingURL=RichTextRenderer.js.map
